import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "../api";
import { Card, CardContent } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Badge } from "../components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger,
} from "../components/ui/dialog";
import {
  Select, SelectTrigger, SelectValue, SelectContent, SelectItem,
} from "../components/ui/select";
import { Plus, Pencil, Trash2, Sparkles } from "lucide-react";

const empty = { label: "", default_amount: "", kind: "deduction", description: "" };

export default function DeductionsLibrary() {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState(empty);
  const [editing, setEditing] = useState(null);
  const [open, setOpen] = useState(false);
  const [seeding, setSeeding] = useState(false);

  const load = () => api.get("/deduction-templates").then((r) => setItems(r.data));
  useEffect(() => { load(); }, []);

  const seedDefaults = async () => {
    if (!window.confirm("Add the common fleet templates (Insurance $85, Escrow $50, Per diem $69, Fuel advance $150, etc.)? Existing labels are left alone.")) return;
    setSeeding(true);
    try {
      const r = await api.post("/deduction-templates/seed-defaults");
      toast.success(`Added ${r.data.created_count} template${r.data.created_count === 1 ? "" : "s"} · ${r.data.skipped_count} already existed`);
      load();
    } catch (e) {
      toast.error("Seed failed");
    } finally {
      setSeeding(false);
    }
  };

  const save = async () => {
    try {
      const payload = { ...form, default_amount: parseFloat(form.default_amount) || 0 };
      if (editing) await api.put(`/deduction-templates/${editing}`, payload);
      else await api.post("/deduction-templates", payload);
      toast.success("Saved");
      setOpen(false);
      load();
    } catch (e) { toast.error("Failed"); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this template?")) return;
    await api.delete(`/deduction-templates/${id}`);
    toast.success("Removed");
    load();
  };

  return (
    <div className="space-y-6" data-testid="deductions-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="eyebrow">Templates</div>
          <h2 className="font-display text-3xl font-bold">Deductions &amp; Reimbursements Library</h2>
          <p className="text-slate-500 mt-1 text-sm">Save re-usable deduction / reimbursement reasons. Pick from this list when logging a trip.</p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={seedDefaults}
            disabled={seeding}
            className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
            data-testid="seed-defaults-btn"
          >
            <Sparkles size={14} className="mr-1" /> {seeding ? "Adding…" : "Add common templates"}
          </Button>
          <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild>
            <Button onClick={() => { setForm(empty); setEditing(null); setOpen(true); }}
                    className="bg-slate-900 hover:bg-slate-800 text-white" data-testid="add-template-btn">
              <Plus size={16} className="mr-2" /> Add Template
            </Button>
          </DialogTrigger>          <DialogContent>
            <DialogHeader>
              <DialogTitle className="font-display">{editing ? "Edit" : "New"} Template</DialogTitle>
            </DialogHeader>
            <div className="space-y-4">
              <div>
                <Label>Label</Label>
                <Input data-testid="template-label" value={form.label} onChange={(e) => setForm({ ...form, label: e.target.value })} />
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label>Kind</Label>
                  <Select value={form.kind} onValueChange={(v) => setForm({ ...form, kind: v })}>
                    <SelectTrigger data-testid="template-kind"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="deduction">Deduction</SelectItem>
                      <SelectItem value="reimbursement">Reimbursement</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Default Amount ($)</Label>
                  <Input data-testid="template-amount" type="number" step="0.01"
                         value={form.default_amount}
                         onChange={(e) => setForm({ ...form, default_amount: e.target.value })} />
                </div>
              </div>
              <div>
                <Label>Description / What is this for?</Label>
                <Textarea data-testid="template-description" rows={3}
                          value={form.description}
                          onChange={(e) => setForm({ ...form, description: e.target.value })} />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
              <Button onClick={save} className="bg-sky-600 hover:bg-sky-700 text-white" data-testid="save-template-btn">Save</Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {items.map((t) => (
          <Card key={t.template_id} className="hover:border-slate-900 transition-colors" data-testid={`template-${t.label.replace(/\s+/g, '-')}`}>
            <CardContent className="p-5">
              <div className="flex items-start justify-between">
                <div>
                  <Badge className={t.kind === "deduction" ? "bg-rose-600" : "bg-emerald-600"}>
                    {t.kind}
                  </Badge>
                  <h3 className="font-display text-lg font-semibold mt-2">{t.label}</h3>
                </div>
                <div className="font-mono text-sm font-semibold">${Number(t.default_amount).toFixed(2)}</div>
              </div>
              {t.description && <p className="text-xs text-slate-500 mt-2">{t.description}</p>}
              <div className="flex justify-end gap-1 mt-3">
                <Button size="icon" variant="ghost" onClick={() => { setForm(t); setEditing(t.template_id); setOpen(true); }}>
                  <Pencil size={15} />
                </Button>
                <Button size="icon" variant="ghost" onClick={() => remove(t.template_id)}>
                  <Trash2 size={15} className="text-rose-600" />
                </Button>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );
}
